import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LINT_WORDS, PART_WORDS, lintByPart, partsToText } from '../../lib/workbench'
import type { DraftOverride, DraftPart, LintFlag, MailThread, WorkbenchDraft } from '../../types/workbench'
import { formatDate } from '../../lib/format'
import { CommentMark, DEFAULT_LAYERS, LayerToggles, OverridesPanel, PartText, VersionStrip, VoiceMark, type Layer } from './DraftLayers'
import { DraftChips, LintChip } from './WorkbenchParts'

// The draft as a mail in draft mode (owner, 2026-09-25: "it needs to look and feel like a draft email"): the To and
// Subject rows, a divider, the body in a mail face, the sign-off — with the parts (opener, insight, ask) marked
// only by a faint guide at the left that shows on hover, and a numbered marker at the end of each part's text
// that opens, on hover or a click, a comment bubble pointing at those words with what stands behind them.
// Typing INTO a part is an override: on blur the changed wording is posted as a turn — "Use this wording: …" —
// so the backend tracks it with its time and the voice learns from it; nothing changes silently. Two ways out,
// as before: "Send to Outbox" hands it to the Outbox for your approval there (nothing sends from here); the chat
// on the left revises it in the same draft.

export function Draft({ draft, to, onToOutbox, onUseWording, onRestore, onTone, busy, handedTo, threads, onThread }: {
  draft: WorkbenchDraft | null; onToOutbox: () => void; onUseWording: (text: string) => void; busy: boolean
  /** the person's threads on file, for "reply in an existing thread"; and the choice (a subject, or null for a fresh thread) */
  threads?: MailThread[]
  onThread?: (subject: string | null) => void
  /** puts a version back as the working draft (REQ-104) */
  onRestore?: (seq: number) => void
  /** a different tone for a part (REQ-106): the instruction goes to the conversation, which revises and records a version */
  onTone?: (instruction: string) => void
  /** who the mail is to: the name, and the address when one is on file */
  to?: { name: string; address?: string | null }
  /** the outbound id once it is in the Outbox */
  handedTo?: string | null
}) {
  const [layers, setLayers] = useState<Layer[]>(DEFAULT_LAYERS)
  const [editing, setEditing] = useState<number | null>(null)
  const [pending, setPending] = useState<Map<number, string>>(new Map())
  const [activeFlag, setActiveFlag] = useState<LintFlag | null>(null)
  const rows = useRef<Map<number, HTMLElement>>(new Map())
  useEffect(() => { setEditing(null); setPending(new Map()); setActiveFlag(null) }, [draft?.text])

  if (!draft) return <p className="muted small">No draft yet. Pick a person on the stage, or say who to write to.</p>
  const on = (l: Layer) => layers.includes(l)
  const toggle = (l: Layer) => setLayers((ls) => (ls.includes(l) ? ls.filter((x) => x !== l) : [...ls, l]))
  const byPart = lintByPart(draft.parts, draft.lint)
  const overrides = draft.overrides ?? []

  const jump = (f: LintFlag) => {
    setActiveFlag(f)
    for (const [i, flags] of byPart) if (flags.some((x) => x.flag === f)) { rows.current.get(i)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); break }
  }

  // an override: the part's new wording, the whole text re-joined, posted as a wording turn
  const commit = (idx: number, text: string) => {
    setEditing(null)
    const was = draft.parts[idx].text
    if (text.trim() === was.trim()) return
    const next = draft.parts.map((p, i) => (i === idx ? { ...p, text } : p))
    setPending((m) => new Map(m).set(idx, text))
    onUseWording(partsToText(next))
  }
  const revert = (o: DraftOverride) => {
    const idx = draft.parts.findIndex((p) => p.part === o.part)
    if (idx < 0) return
    const next = draft.parts.map((p, i) => (i === idx ? { ...p, text: o.before } : p))
    onUseWording(partsToText(next))
  }

  const shownPart = (p: DraftPart, idx: number): DraftPart => {
    const origin = pending.has(idx) ? 'override' : p.origin ?? 'model'
    return pending.has(idx) ? { ...p, text: pending.get(idx)!, origin: 'override' } : { ...p, origin }
  }
  /** one part as editable text with its comment marker at the end of its words */
  const partBody = (p: DraftPart, idx: number) => {
    const flags = byPart.get(idx) ?? []
    const hit = (activeFlag ? flags.find((x) => x.flag === activeFlag) : undefined) ?? flags[0]
    const shown = shownPart(p, idx)
    return (
      <>
        {editing === idx ? (
          <textarea className="part-edit" defaultValue={shown.text} rows={Math.max(2, Math.ceil(shown.text.length / 70))} autoFocus aria-label={`${PART_WORDS[p.part]}, in your words`} disabled={busy}
            onBlur={(e) => commit(idx, e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setEditing(null) } }} />
        ) : (
          <span className="part-run">
            {/* a span in the text's own flow, not a button: a button is one unbreakable box, and the marker after it
                fell to the next line — here the marker follows the last word */}
            <span role="button" tabIndex={busy || handedTo ? -1 : 0} aria-disabled={busy || !!handedTo} className={`part-click ${busy || handedTo ? 'disabled' : ''}`}
              onClick={() => !busy && !handedTo && setEditing(idx)} onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy && !handedTo) { e.preventDefault(); setEditing(idx) } }}
              title="Click to type your own words here; the change is kept as yours">
              <PartText part={shown} span={hit?.span} note={hit ? `${LINT_WORDS[hit.flag.kind] ?? hit.flag.kind}: ${hit.flag.text}` : undefined} />
            </span>
            {(on('comments') || on('sources')) && <CommentMark part={shown} flags={on('comments') ? flags : []} showSources={on('sources')} activeFlag={activeFlag} onFlag={jump} showOrigin={on('overlays')} />}
            {on('comments') && <VoiceMark part={shown} onTone={handedTo ? undefined : onTone} busy={busy} />}
          </span>
        )}
      </>
    )
  }
  const subjectIdx = draft.parts.findIndex((p) => p.part === 'subject')
  const bodyParts = draft.parts.map((p, idx) => ({ p, idx })).filter((x) => x.p.part !== 'subject')

  // THE COMPOSE WINDOW (owner, 2026-10-12 B: "the draft still doesn't look like an editable email window"): a window bar
  // naming the message, the To / Cc / Subject fields as fields, the body as an editable page with a caret, and the
  // window's own foot with the one way out — Send to Outbox. What Scout knows ABOUT the draft (its chips, versions,
  // lint, cautions) sits above the window, never inside the mail.
  const title = handedTo ? 'In your Outbox' : draft.thread ? `Reply · ${draft.thread.subject}` : 'New message'
  return (
    <div className="wb-draft layered">
      <div className="draft-about">
        <DraftChips draft={draft} />
        <VersionStrip versions={draft.versions ?? []} onRestore={handedTo ? undefined : onRestore} busy={busy} />
        {draft.lint.length > 0 && !on('comments') && (
          <div className="lint-strip">{draft.lint.map((f: LintFlag, i) => <LintChip key={i} flag={f} active={activeFlag === f} onClick={() => jump(f)} />)}</div>
        )}
        {draft.cautions.length > 0 && (
          <details className="cautions small">
            <summary><strong>Keep out of the email</strong> · {draft.cautions.length} {draft.cautions.length === 1 ? 'thing' : 'things'} Scout is holding back</summary>
            <ul className="plain cautions-list">{draft.cautions.map((c, i) => <li key={i}>{c}</li>)}</ul>
          </details>
        )}
      </div>

      <div className={`mail-window ${handedTo ? 'handed' : ''}`} role="group" aria-label="The draft email">
        <div className="mail-window-bar">
          <span className="mail-window-dots" aria-hidden="true"><i /><i /><i /></span>
          <span className="mail-window-title">{title}</span>
          <LayerToggles on={layers} onToggle={toggle} />
        </div>
        <div className={`mail-draft ${on('overlays') ? 'with-overlays' : ''}`}>
          <div className="mail-head">
            <div className="mail-field"><span className="mail-label">To</span><span className="mail-value"><span className="mail-recipient">{to?.name ?? 'the person on the stage'}{to?.address ? <span className="muted"> &lt;{to.address}&gt;</span> : null}</span></span></div>
            <div className="mail-field"><span className="mail-label">Cc</span><span className="mail-value muted"> </span></div>
            <div className="mail-field subject" ref={(el) => { if (subjectIdx >= 0) { if (el) rows.current.set(subjectIdx, el); else rows.current.delete(subjectIdx) } }}>
              <span className="mail-label">Subject</span>
              <span className="mail-value">{subjectIdx >= 0 ? partBody(draft.parts[subjectIdx], subjectIdx) : <span className="muted">(no subject)</span>}</span>
            </div>
          </div>
          <div className="mail-body" title={handedTo ? undefined : 'Click any line to type your own words'}>
            {bodyParts.map(({ p, idx }) => (
              <div key={idx} className={`mail-para part-${p.part} origin-${shownPart(p, idx).origin}`} ref={(el) => { if (el) rows.current.set(idx, el); else rows.current.delete(idx) }}>
                <span className="part-guide" aria-hidden="true">{PART_WORDS[p.part]}</span>
                {partBody(p, idx)}
              </div>
            ))}
            {!handedTo && editing === null && <span className="mail-caret" aria-hidden="true" />}
          </div>
        </div>
        <div className="mail-foot">
          {handedTo ? (
            <p className="small outbox-line" role="status">It's in your <Link to="/outbox">Outbox</Link> — send it from there.</p>
          ) : (
            <button type="button" className="button primary mail-send" onClick={onToOutbox} disabled={busy || editing !== null} title="Puts the message in your Outbox, where you approve it and send it yourself. Nothing sends from here.">Put in my Outbox</button>
          )}
          {!handedTo && <span className="muted small draft-hint">Click any line to type your own words; tell the Wordsmith what to change.</span>}
        </div>
      </div>

      {onThread && <ThreadStrip draft={draft} threads={threads ?? []} onThread={onThread} busy={busy || !!handedTo} />}

      {on('overrides') && (
        <div className="overrides-panel">
          <p className="kicker">Your words over the draft</p>
          <OverridesPanel overrides={overrides} onRevert={handedTo ? undefined : revert} busy={busy} />
        </div>
      )}
    </div>
  )
}

/* ── the thread it replies in (owner, 2026-10-12) ──
   Under the email: when the draft replies in an existing thread, a brief of what is being answered with "Reply in a
   different thread" and "Start a fresh thread"; when it starts fresh, "Reply in an existing thread…" opens the person's
   threads on file to pick from. Either choice re-threads the draft: the subject follows and the message is revised. */
export function ThreadStrip({ draft, threads, onThread, busy }: { draft: WorkbenchDraft; threads: MailThread[]; onThread: (subject: string | null) => void; busy: boolean }) {
  const [picking, setPicking] = useState(false)
  const t = draft.thread
  const choose = (subject: string | null) => { setPicking(false); onThread(subject) }
  const others = t ? threads.filter((x) => x.subject !== t.subject) : threads
  return (
    <div className="thread-strip" role="group" aria-label="The thread this email replies in">
      {t ? (
        <>
          <p className="small thread-line">
            <span className="tag">Replying in</span> <strong>{t.subject}</strong>
            <span className="muted"> · {t.direction === 'inbound' ? `${t.lastFrom ?? 'they'} wrote last` : 'you wrote last'}, {formatDate(t.lastAt)}</span>
          </p>
          <p className="muted small thread-excerpt">“{t.excerpt}”</p>
          <p className="thread-actions">
            {others.length > 0 && <button type="button" className="button small" onClick={() => setPicking((v) => !v)} disabled={busy} title="Pick another of your threads with them to reply in; the subject and the draft follow.">Reply in a different thread…</button>}
            <button type="button" className="button small" onClick={() => choose(null)} disabled={busy} title="Not a reply: a new thread with its own subject, and the draft rewritten to open fresh.">Start a fresh thread instead</button>
          </p>
        </>
      ) : (
        <p className="thread-actions">
          <span className="muted small">A fresh thread — not a reply to anything on file.</span>
          {threads.length > 0
            ? <button type="button" className="button small" onClick={() => setPicking((v) => !v)} disabled={busy} title="Reply inside a thread you already have with them, so it lands in context; the subject and the draft follow.">Reply in an existing thread…</button>
            : <span className="muted small">No threads with them on file to reply in.</span>}
        </p>
      )}
      {picking && (
        <ul className="plain thread-picker" aria-label="Your threads with them">
          {others.map((x) => (
            <li key={x.subject}>
              <button type="button" className="link-button" onClick={() => choose(x.subject)} disabled={busy}>
                <strong>{x.subject}</strong>
                <span className="muted small"> · {x.direction === 'inbound' ? 'they wrote last' : 'you wrote last'}, {formatDate(x.lastAt)}{x.unansweredFollowUps ? ` · ${x.unansweredFollowUps} of yours unanswered` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
